<!--
  This component is a dialog that is used in the projects index page and its purpose is to help the user
  load a previously upload KPIs or PSIs list into a project.
-->
<template>
  <v-dialog persistent max-width="800px">
    <template v-slot:activator="{ props: dialogActivatorProps }">
      <v-tooltip text="Upload KPI/PSI" location="top">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-btn
              v-bind="dialogActivatorProps, tooltipProps"
              class="mx-1 border-md "
              size="small"
              rounded
              flat
              height="35px"
              min-width="35px"
              max-width="35px"
          ><i class="fas fa-upload text-blue-darken-4"/></v-btn>
        </template>
      </v-tooltip>
    </template>

    <template v-slot:default="{ isActive }">
      <v-card>
        <v-card-title><b>Upload KPIs/PSIs to: {{ project.name }} </b></v-card-title>
        <v-divider/>
        <v-form class="pa-5">
          <v-select
              v-model="selectedMethod"
              bg-color="#f1f1f5"
              label="Select method"
              :items="methodOptions"
              variant="solo"
              density="comfortable"
          >
          </v-select>

          <v-select
              v-model="selectedFile"
              variant="solo"
              bg-color="#e8e8f5"
              density="comfortable"
              :items="fileOptions"
          >
          </v-select>

          <div v-if="isProgressBarActive" style="width: 50%; margin: 5px 0 0 12px">
            <v-progress-linear
                color="blue"
                indeterminate
            ></v-progress-linear>
            <p class="text-caption">Please wait...</p>
          </div>

        </v-form>
        <v-divider/>
        <v-card-actions justify="center">
          <v-btn
            color="red"
            class="px-3"
            rounded
            text="Close"
            @click="closeDialog(isActive)"
          />

          <v-btn
              :disabled="!isLoadActive"
              type="button"
              class="px-4"
              color="blue-darken-4"
              rounded
              prepend-icon="fas fa-check mr-1"
              variant="flat"
              text="Load"
              @click="submitForm()"
          />
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup>

import {computed, ref} from "vue";
import * as $routes from "@/packs/routes";
import {router} from "@inertiajs/vue3";

const props = defineProps({
  /**
   * The project in which the list will be loaded into.
   * It should contain at least the id and the name of the project.
   */
  project: {
    type: Object,
    required: true
  },
  /**
   * The available KPI or PSI lists previously uploaded by the user.
   * The format of the prop is the following:
   * `{ kpi: [... list of file names (strings) ...], psi: [... list of file names (strings) ...] }`
   */
  availableFiles: {
    type: Object,
    required: true
  }
})

const selectedMethod = ref(null)
const methodOptions = [
  {title: 'KPI', value: 'kpi'},
  {title: 'PSI', value: 'psi'},
]
const fileOptions = computed(() => {
  return props.availableFiles[selectedMethod.value]
})
const selectedFile = ref(null)
const isLoadActive = computed(() => {
  return selectedFile.value !== null && !isProgressBarActive.value
})
const isProgressBarActive = ref(false)
/**
 * Decides dynamically the route where the data will be posted. The route differs whether
 * the user uploads a KPI or a PSI list. The KPI route is returned as default value
 * if no data have been entered in the form.
 */
const formRoute = computed(() => {
  const routes = {kpi: $routes.file_selection, psi: $routes.psi_file_selection}
  if (selectedMethod.value === null) {
    return routes.kpi(props.project.id)
  } // Return default if nothing is selected yet.

  return routes[selectedMethod.value](props.project.id)
})

/**
 * Closes the dialog and resets the provided inputs.
 * @param {ref} isActive - A ref object with boolean value. It is necessary because the dialog uses its own
 * scopes variable that decides its visibility.
 */
function closeDialog(isActive) {
  if(!isProgressBarActive.value) {
    isActive.value = false
    selectedMethod.value = null
    selectedFile.value = null
  }
}

/**
 * Submits the form data.
 */
function submitForm() {
  isProgressBarActive.value = true
  router.post(formRoute.value, {fname: selectedFile.value}, {
    onFinish: () => {
      isProgressBarActive.value = false
    },
  })
}
</script>